import { createClient } from '@supabase/supabase-js';
import Peer from 'peerjs';
import { io, Socket } from 'socket.io-client';
import { v4 as uuidv4 } from 'uuid';

const supabase = createClient(
  import.meta.env.VITE_SUPABASE_URL || '',
  import.meta.env.VITE_SUPABASE_ANON_KEY || ''
);

let peer: Peer | null = null;
let socket: Socket | null = null;
let reconnectAttempts = 0;
const MAX_RECONNECT_ATTEMPTS = 5;
const INITIAL_TIMEOUT = 60000; // 60 seconds for initial connection
const MAX_DELAY = 30000; // Maximum delay of 30 seconds
const SOCKET_CONNECT_TIMEOUT = 120000; // Increased to 120 seconds
const TRANSPORT_TIMEOUT = 30000; // Increased to 30 seconds per transport attempt
const HEARTBEAT_INTERVAL = 25000; // 25 seconds heartbeat interval

function getExponentialDelayWithJitter() {
  const baseDelay = Math.min(1000 * Math.pow(2, reconnectAttempts), MAX_DELAY);
  // Add random jitter between 0-1000ms to prevent synchronized retries
  const jitter = Math.random() * 1000;
  return baseDelay + jitter;
}

async function setupPeerConnection(userId: string, stream: MediaStream) {
  const peerId = `${userId}-${uuidv4()}`;
  
  if (peer) {
    peer.destroy();
  }

  return new Promise<Peer>((resolve, reject) => {
    const newPeer = new Peer(peerId, {
      config: {
        iceServers: [
          { urls: 'stun:stun.l.google.com:19302' },
          { urls: 'stun:global.stun.twilio.com:3478' }
        ]
      },
      secure: true,
      debug: 2
    });

    newPeer.on('open', () => {
      console.log('Peer connection opened:', peerId);
      resolve(newPeer);
    });

    newPeer.on('error', (error) => {
      console.error('Peer connection error:', error);
      if (error.type === 'peer-unavailable') {
        reject(new Error('Peer is not available'));
      } else if (error.type === 'network') {
        const delay = getExponentialDelayWithJitter();
        setTimeout(() => {
          setupPeerConnection(userId, stream)
            .then(resolve)
            .catch(reject);
        }, delay);
      } else {
        reject(error);
      }
    });

    setTimeout(() => {
      if (!newPeer.connected) {
        newPeer.destroy();
        reject(new Error('Peer connection timeout'));
      }
    }, INITIAL_TIMEOUT);
  });
}

async function setupSocketConnection(userId: string, expertId: string) {
  if (socket) {
    socket.disconnect();
  }

  const socketUrl = import.meta.env.VITE_SOCKET_URL;
  if (!socketUrl) {
    throw new Error('Socket URL is not configured');
  }

  console.log('Attempting socket connection to:', socketUrl);

  // Create socket instance with initial configuration
  const newSocket = io(socketUrl, {
    query: { userId, expertId },
    transports: ['websocket', 'polling'],
    reconnection: true,
    reconnectionAttempts: MAX_RECONNECT_ATTEMPTS,
    reconnectionDelay: getExponentialDelayWithJitter(),
    timeout: SOCKET_CONNECT_TIMEOUT,
    forceNew: true,
    autoConnect: false,
    pingTimeout: 30000, // Increased ping timeout
    pingInterval: HEARTBEAT_INTERVAL
  });

  return new Promise<Socket>((resolve, reject) => {
    let connectionTimeout: NodeJS.Timeout;
    let transportTimeout: NodeJS.Timeout;
    let heartbeatInterval: NodeJS.Timeout;
    let currentTransportIndex = 0;
    const transports = ['websocket', 'polling'];

    const cleanup = () => {
      clearTimeout(connectionTimeout);
      clearTimeout(transportTimeout);
      clearInterval(heartbeatInterval);
      newSocket.off('connect');
      newSocket.off('connect_error');
      newSocket.off('disconnect');
      newSocket.off('error');
      newSocket.off('pong');
    };

    // Set overall connection timeout
    connectionTimeout = setTimeout(() => {
      cleanup();
      newSocket.disconnect();
      reject(new Error('Socket connection timeout - all transport attempts failed'));
    }, SOCKET_CONNECT_TIMEOUT);

    const tryNextTransport = () => {
      if (currentTransportIndex >= transports.length) {
        reconnectAttempts++;
        if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
          cleanup();
          reject(new Error('Maximum reconnection attempts reached'));
          return;
        }
        currentTransportIndex = 0;
        const delay = getExponentialDelayWithJitter();
        console.log(`Retrying all transports in ${delay}ms... (Attempt ${reconnectAttempts + 1} of ${MAX_RECONNECT_ATTEMPTS})`);
        setTimeout(tryNextTransport, delay);
        return;
      }

      const transport = transports[currentTransportIndex];
      console.log(`Attempting connection with transport: ${transport}`);
      
      newSocket.io.opts.transports = [transport];
      
      // Clear previous transport timeout
      clearTimeout(transportTimeout);
      
      // Set timeout for current transport attempt
      transportTimeout = setTimeout(() => {
        console.log(`Transport ${transport} timed out`);
        currentTransportIndex++;
        if (!newSocket.connected) {
          newSocket.disconnect();
          tryNextTransport();
        }
      }, TRANSPORT_TIMEOUT);

      // Attempt connection with current transport
      newSocket.connect();
    };

    // Setup heartbeat mechanism
    const setupHeartbeat = () => {
      heartbeatInterval = setInterval(() => {
        if (newSocket.connected) {
          const start = Date.now();
          newSocket.emit('ping');
          newSocket.once('pong', () => {
            const latency = Date.now() - start;
            console.log(`Socket heartbeat latency: ${latency}ms`);
          });
        }
      }, HEARTBEAT_INTERVAL);
    };

    newSocket.on('connect', () => {
      const transport = newSocket.io.engine.transport.name;
      console.log('Socket connected successfully using', transport);
      cleanup();
      reconnectAttempts = 0;
      setupHeartbeat();
      resolve(newSocket);
    });

    newSocket.on('connect_error', (error) => {
      console.error('Socket connection error:', error.message);
      currentTransportIndex++;
      tryNextTransport();
    });

    newSocket.on('error', (error) => {
      console.error('Socket error:', error);
      if (error.type === 'TransportError') {
        currentTransportIndex++;
        tryNextTransport();
      }
    });

    newSocket.on('disconnect', (reason) => {
      console.log('Socket disconnected:', reason);
      cleanup();
      if (reason === 'io server disconnect' || reason === 'transport close') {
        const delay = getExponentialDelayWithJitter();
        console.log(`Attempting reconnection in ${delay}ms...`);
        setTimeout(() => {
          currentTransportIndex = 0;
          tryNextTransport();
        }, delay);
      }
    });

    // Start with first transport
    tryNextTransport();
  });
}

export async function initializeVideoCall(userId: string, expertId: string) {
  try {
    console.log('Initializing video call for user:', userId, 'with expert:', expertId);
    
    const stream = await navigator.mediaDevices.getUserMedia({
      video: true,
      audio: true
    });

    // Initialize connections with retry logic
    let attempts = 0;
    const maxAttempts = 3;

    while (attempts < maxAttempts) {
      try {
        console.log(`Connection attempt ${attempts + 1} of ${maxAttempts}`);
        const [newPeer, newSocket] = await Promise.all([
          setupPeerConnection(userId, stream),
          setupSocketConnection(userId, expertId)
        ]);

        peer = newPeer;
        socket = newSocket;
        console.log('Successfully established both peer and socket connections');
        break;
      } catch (error) {
        attempts++;
        console.error(`Attempt ${attempts} failed:`, error);
        if (attempts === maxAttempts) {
          throw error;
        }
        // Wait before retrying
        const delay = getExponentialDelayWithJitter();
        console.log(`Waiting ${delay}ms before next attempt...`);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }

    if (!peer || !socket) {
      throw new Error('Failed to initialize video call connections');
    }

    peer.on('call', (call) => {
      call.answer(stream);
      call.on('stream', (remoteStream) => {
        const remoteVideo = document.getElementById('remote-video') as HTMLVideoElement;
        if (remoteVideo) {
          remoteVideo.srcObject = remoteStream;
          remoteVideo.play().catch(error => console.error('Error playing remote video:', error));
        }
      });
    });

    const localVideo = document.getElementById('local-video') as HTMLVideoElement;
    if (localVideo) {
      localVideo.srcObject = stream;
      localVideo.play().catch(error => console.error('Error playing local video:', error));
    }

    return { peer, stream, socket };
  } catch (error) {
    console.error('Error initializing video call:', error);
    await endVideoCall();
    throw error;
  }
}

export async function endVideoCall() {
  try {
    if (peer) {
      peer.destroy();
      peer = null;
    }

    if (socket) {
      socket.disconnect();
      socket = null;
    }

    const localVideo = document.getElementById('local-video') as HTMLVideoElement;
    const remoteVideo = document.getElementById('remote-video') as HTMLVideoElement;

    if (localVideo?.srcObject) {
      const tracks = (localVideo.srcObject as MediaStream).getTracks();
      tracks.forEach(track => track.stop());
      localVideo.srcObject = null;
    }

    if (remoteVideo?.srcObject) {
      const tracks = (remoteVideo.srcObject as MediaStream).getTracks();
      tracks.forEach(track => track.stop());
      remoteVideo.srcObject = null;
    }

    reconnectAttempts = 0;
  } catch (error) {
    console.error('Error ending video call:', error);
    throw error;
  }
}

export function toggleAudioVideo(stream: MediaStream, type: 'audio' | 'video', enabled: boolean) {
  try {
    stream.getTracks()
      .filter(track => track.kind === type)
      .forEach(track => track.enabled = enabled);
  } catch (error) {
    console.error(`Error toggling ${type}:`, error);
    throw error;
  }
}